import React, { useRef } from "react";
import {
  FaWrench,
  FaCogs,
  FaRuler,
  FaChartLine,
  FaCheckCircle,
  FaToolbox,
  FaIndustry,
  FaUserGraduate,
  FaCalendarAlt,
  FaAward,
} from "react-icons/fa";

const FitterCourseSection = () => {
  const cardRefs = useRef([]);
  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (card) {
      card.style.transform = "perspective(1000px) rotateX(0) rotateY(0) scale(1)";
    }
  };

  const keywords = [
    "Precision Fitting",
    "Machine Tools",
    "Measuring Instruments",
    "Workshop Practice",
    "Drilling Machine",
    "Screw Cutting",
    "Lathe Operations",
    "Grinding",
    "Welding Basics",
    "CAD Drawing",
  ];

  const highlights = [
    "Hands‑on training in modern workshops",
    "Expert faculty with industry experience",
    "100% placement assistance",
    "Industry‑recognized certification",
    "Live project work",
    "Soft skills development",
  ];

  const skills = [
    { name: "Precision Measurement", level: 95 },
    { name: "Machine Operation", level: 90 },
    { name: "Blueprint Reading", level: 85 },
    { name: "Assembly Techniques", level: 92 },
  ];

  const tools = [
    { name: "Vernier Caliper", icon: FaRuler },
    { name: "Micrometer", icon: FaToolbox },
    { name: "Drilling Machine", icon: FaIndustry },
    { name: "Lathe Machine", icon: FaCogs },
  ];

  const careerPaths = [
    { role: "Fitter", companies: "Tata Motors, Mahindra", salary: "2.5 - 4 LPA" },
    { role: "Maintenance Technician", companies: "Manufacturing Plants", salary: "2.8 - 4.5 LPA" },
    { role: "Quality Inspector", companies: "Automobile Industry", salary: "3 - 5 LPA" },
    { role: "Workshop Supervisor", companies: "Defense, Railways", salary: "4 - 6 LPA" },
  ];

  return (
    <section className="w-full overflow-hidden bg-gradient-to-br from-green-50 via-white to-orange-50">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 text-sm text-gray-500">
          <span className="text-green-600">Home</span> / About RDS /{" "}
          <span className="text-orange-500 font-semibold">FITTER</span>
        </div>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-8 items-start">
          <div className="space-y-6">
            <div className="inline-block rounded-full bg-green-100 px-4 py-1 text-sm font-semibold text-green-700">
              DURATION 2 YEARS | FULL TIME
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl">
              About the <span className="text-green-600">Course</span>
            </h2>
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">
              On completion of the course, the trainee shall be able to independently prepare
              machine components using skills such as filling, hack sawing, drilling, chipping,
              reaming, screw cutting, and scrapping. They will individually handle precision
              measuring instruments, read machine drawings, and operate various fitter tools
              and machines like drilling machines, lathes, and grinders.
            </p>
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">
              The curriculum emphasizes hands‑on training in a modern workshop environment,
              safety protocols, and industry‑standard practices. Graduates are equipped for
              roles in manufacturing, automotive, and maintenance sectors.
            </p>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Skills Covered</h3>
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm transition-all hover:scale-105 hover:bg-green-100 hover:text-green-700 cursor-default"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Why Choose This Course?</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {highlights.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 group transition hover:translate-x-1">
                    <FaCheckCircle className="mt-1 h-4 w-4 text-green-600 group-hover:scale-110" />
                    <span className="text-sm text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Core Competencies</h3>
              <div className="space-y-3">
                {skills.map((skill, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm">
                      <span>{skill.name}</span>
                      <span>{skill.level}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-green-500 to-orange-500 transition-all duration-1000"
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="sticky top-24 space-y-6">
              <div className="group relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="image8.png"
                  alt="Fitter at work"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/80 to-transparent">                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-white p-4 text-center shadow-md transition-all hover:scale-105 hover:shadow-lg">
                  <FaUserGraduate className="mx-auto h-6 w-6 text-green-600" />
                  <p className="text-2xl font-bold text-gray-800">500+</p>
                  <p className="text-xs text-gray-500">Students Trained</p>
                </div>
                <div className="rounded-xl bg-white p-4 text-center shadow-md transition-all hover:scale-105 hover:shadow-lg">
                  <FaAward className="mx-auto h-6 w-6 text-orange-500" />
                  <p className="text-2xl font-bold text-gray-800">90%</p>
                  <p className="text-xs text-gray-500">Placement Rate</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Tools & Equipment</h3>
                <div className="grid grid-cols-2 gap-3">
                  {tools.map((tool, idx) => (
                    <div
                      key={idx}
                      ref={(el) => (cardRefs.current[idx] = el)}
                      className="flex items-center gap-2 rounded-lg bg-white p-3 shadow transition-all duration-300 cursor-default"
                      onMouseMove={(e) => handleMouseMove(e, idx)}
                      onMouseLeave={() => handleMouseLeave(idx)}
                    >
                      <tool.icon className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">{tool.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl">
              Career <span className="text-orange-500">Opportunities</span>
            </h2>
            <p className="mt-2 text-gray-600">After completing the FITTER course, you can explore these roles</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {careerPaths.map((job, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-md transition-all hover:shadow-xl hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <h3 className="text-xl font-bold text-gray-800">{job.role}</h3>
                <p className="mt-2 text-sm text-gray-600">{job.companies}</p>
                <p className="mt-2 text-sm font-semibold text-green-600">{job.salary}</p>
              </div>
            ))}
          </div>
        </div>

        <div id="apply" className="mt-20 rounded-3xl bg-gradient-to-r from-green-600 to-orange-500 p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-bold md:text-3xl">Ready to start your journey?</h3>
          <p className="mt-2 text-lg opacity-90">Limited seats available for the upcoming batch.</p>
          <button className="mt-6 rounded-full bg-white px-8 py-3 font-semibold text-green-600 shadow-md transition hover:scale-105 hover:shadow-lg">
            Apply for Admission
          </button>
        </div>
      </div>
      <style jsx>{`
        @keyframes slowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slowZoom {
          animation: slowZoom 20s ease-in-out infinite alternate;
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </section>
  );
};

export default FitterCourseSection;